'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useMemo, useRef } from 'react';

import { useAnalyticsTrack } from '@/components/analytics/hooks';

type AnalyticsEventName = 'View' | 'Input' | 'Submit' | 'Click' | 'Select';

export type InvoiceInformationFieldName =
    | 'accountType'
    | 'personalName'
    | 'companyName'
    | 'vatNumber'
    | 'street'
    | 'city'
    | 'postalCode'
    | 'countryCode';

type Options = {
    formType?: 'checkout' | 'settings';
};

function now() {
    return Date.now();
}

function genFormId() {
    return `inv_${Math.random().toString(36).slice(2, 11)}_${Date.now().toString(36)}`;
}

function getPagePath(pathname?: string | null, searchParams?: URLSearchParams | null) {
    const search = searchParams?.toString();
    return search ? `${pathname ?? ''}?${search}` : (pathname ?? '');
}

export function useInvoiceInformationFormAnalytics({ formType = 'checkout' }: Options = {}) {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const trackEvent = useAnalyticsTrack();

    const formIdRef = useRef(genFormId());
    const startedFieldsRef = useRef<Set<InvoiceInformationFieldName>>(new Set());

    const pagePath = useMemo(() => getPagePath(pathname, searchParams), [pathname, searchParams]);

    const common = useCallback(
        (extra?: Record<string, unknown>) => ({
            form_id: formIdRef.current,
            form_type: formType,
            page_path: pagePath,
            ts: now(),
            ...(extra ?? {}),
        }),
        [formType, pagePath]
    );

    const track = useCallback(
        (
            event: AnalyticsEventName,
            {
                action,
                category = 'Invoice Information',
                interaction = event,
                ...props
            }: Record<string, unknown> & {
                action: string;
                category?: string;
                interaction?: AnalyticsEventName;
            }
        ) => {
            trackEvent(event, {
                category,
                action,
                interaction,
                ...props,
            });
        },
        [trackEvent]
    );

    const trackFieldStarted = useCallback(
        (field: InvoiceInformationFieldName) => {
            if (startedFieldsRef.current.has(field)) return;

            startedFieldsRef.current.add(field);

            track('Input', {
                action: `Field Started - ${field}`,
                category: 'Invoice Information',
                ...common({
                    field,
                }),
            });
        },
        [common, track]
    );

    const trackFormSubmitted = useCallback(
        (meta?: {
            accountType?: string;
            isCheckoutForm?: boolean;
            hasVatNumber?: boolean;
            hasInitialValues?: boolean;
        }) => {
            track('Submit', {
                action: 'Form Submitted',
                category: 'Invoice Information',
                ...common({
                    account_type: meta?.accountType ?? 'unknown',
                    is_checkout_form: meta?.isCheckoutForm ?? false,
                    has_vat_number: meta?.hasVatNumber ?? false,
                    has_initial_values: meta?.hasInitialValues ?? false,
                }),
            });
        },
        [common, track]
    );

    return {
        trackFieldStarted,
        trackFormSubmitted,
    };
}
